import type { Directive, DirectiveBinding } from 'vue'
import { useAuthStore } from '../stores/authStore'

type RoleAction = 'view' | 'create' | 'edit' | 'delete'

interface RoleBinding {
  action: RoleAction
  hide?: boolean // If true, hide element; if false, just disable
}

/**
 * Vue directive for role-based element control
 * 
 * Usage:
 * v-role="{ action: 'create' }"
 * v-role="{ action: 'delete', hide: true }"
 */
export const roleDirective: Directive = {
  mounted(el: HTMLElement, binding: DirectiveBinding<RoleBinding>) {
    checkRole(el, binding)
  },
  updated(el: HTMLElement, binding: DirectiveBinding<RoleBinding>) {
    checkRole(el, binding)
  }
}

function checkRole(el: HTMLElement, binding: DirectiveBinding<RoleBinding>) {
  const { action, hide = false } = binding.value
  const authStore = useAuthStore()
  const role = authStore.user?.role || 'viewer'

  // Check role-based permission
  let hasAccess = false
  
  switch (action) {
    case 'view':
      hasAccess = true // Everyone can view
      break
    case 'create':
    case 'edit':
      hasAccess = role === 'admin' || role === 'manager'
      break
    case 'delete':
      hasAccess = role === 'admin'
      break
  }

  if (!hasAccess) {
    if (hide) {
      el.style.display = 'none'
    } else {
      if (el instanceof HTMLButtonElement || el instanceof HTMLInputElement) {
        el.disabled = true
        el.classList.add('opacity-50', 'cursor-not-allowed')
      } else {
        el.classList.add('pointer-events-none', 'opacity-50')
      }
      el.title = 'คุณไม่มีสิทธิ์ในการดำเนินการนี้'
    }
  } else {
    if (hide) {
      el.style.display = ''
    } else {
      if (el instanceof HTMLButtonElement || el instanceof HTMLInputElement) {
        el.disabled = false
        el.classList.remove('opacity-50', 'cursor-not-allowed')
      } else {
        el.classList.remove('pointer-events-none', 'opacity-50')
      }
      el.title = ''
    }
  }
}

/**
 * Simplified directive for admin-only elements
 * 
 * Usage:
 * v-admin
 */
export const adminDirective: Directive = {
  mounted(el: HTMLElement) {
    checkAdmin(el)
  },
  updated(el: HTMLElement) {
    checkAdmin(el)
  }
}

function checkAdmin(el: HTMLElement) {
  const authStore = useAuthStore()
  const isAdmin = authStore.user?.role === 'admin'

  if (!isAdmin) {
    el.style.display = 'none'
  } else {
    el.style.display = ''
  }
}
